import { useEffect, useMemo, useState } from "react";
import type { GroupView, GroupTicketView } from "@/services/types";
import { groupService } from "@/services";
import { useToast } from "@/context/ToastContext";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { Select } from "@/components/base/Select";
import { Alert } from "@/components/base/Alert";

interface ChangeTicketModalProps {
  open: boolean;
  group: GroupView;
  ticket: GroupTicketView | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function ChangeTicketModal({ open, group, ticket, onClose, onSuccess }: ChangeTicketModalProps) {
  const toast = useToast();
  const [target, setTarget] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTarget("");
    setError(null);
  }, [open, ticket?.seat]);

  const options = useMemo(
    () =>
      group.tickets
        .filter((t) => t.seat !== ticket?.seat)
        .map((t) => ({
          value: String(t.seat),
          label: t.member ? `#${t.seat} — ${t.member.fullName} (swap)` : `#${t.seat} — Empty`,
        })),
    [group.tickets, ticket?.seat]
  );

  const targetTicket = group.tickets.find((t) => String(t.seat) === target);

  if (!ticket || !ticket.member) return null;

  const handleSubmit = async () => {
    if (!ticket.groupMemberId || !target) return;
    setSubmitting(true);
    setError(null);
    try {
      await groupService.changeTicketNo(group.id, ticket.groupMemberId, Number(target));
      toast.success(
        "Ticket number changed",
        targetTicket?.member
          ? `${ticket.member!.fullName} is now on ticket #${target}; ${targetTicket.member.fullName} moved to #${ticket.seat}.`
          : `${ticket.member!.fullName} moved from ticket #${ticket.seat} to #${target}.`
      );
      onClose();
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to change ticket number.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={`Change ticket number — #${ticket.seat}`}
      description={`Move ${ticket.member.fullName} to another ticket in this group.`}
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!target || submitting}
            icon={submitting ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-check-line" />}
          >
            {submitting ? "Saving…" : targetTicket?.member ? "Swap tickets" : "Change ticket"}
          </Button>
        </div>
      }
    >
      {/* Modal body scrolls (overflow-auto), which clips the open dropdown regardless of z-index — reserve room for it. */}
      <div className="min-h-[20rem] space-y-4">
        {error ? (
          <Alert variant="error" title="Could not change ticket">
            {error}
          </Alert>
        ) : null}

        <Select
          label="New ticket number"
          placeholder="Select a ticket"
          value={target}
          onChange={setTarget}
          options={options}
          searchable
          required
        />

        {targetTicket?.member ? (
          <Alert variant="warning" title="Tickets will be swapped">
            Ticket #{target} is held by {targetTicket.member.fullName}. They will move to ticket #{ticket.seat}.
          </Alert>
        ) : null}

        {group.status === "active" ? (
          <p className="text-xs text-foreground-500">
            Payments, installments and auction history stay with the member. Receipts already printed keep the old ticket
            number in their text.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
